import React from 'react';
import { ENotificationTypes } from '../../Types';
import {
  lineStatus,
  getStartEndOfLines,
  dateDifference,
  groupArrayByKey,
} from '../../utils';
import { useLinesContext } from '../../contexts/Lines';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { useNotificationsContext } from '../../contexts/Notifications';
import { useHistory } from 'react-router';
import { ILine } from '../../global';
import cloneDeep from 'lodash.clonedeep';
import messages from '../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import moment from 'moment';
import Button from '../BuildingBlocks/Button/Button';

const useStyles = makeStyles((theme: any) => ({
  root: {
    minWidth: 175,
    position: 'fixed',
    right: 100,
    bottom: 30,
    zIndex: 1000,
    fontWeight: 700,
  },
}));

export const buttonText = 'Review and submit';

export default function SubmitApplicationButton() {
  const classes = useStyles();
  const history = useHistory();
  const snackbar = useSnackbarContext();
  const notification = useNotificationsContext();
  let existingPlan = null;

  const {
    isLineSelected,
    lines,
    setLines,
    applications,
    isCreatingFirst3Lines,
  } = useLinesContext();

  const hasDraftLine =
    lines?.filter((line) => line.status === lineStatus.draft && !line.new)
      .length > 0
      ? true
      : false;

  function unselectedLines() {
    const unselectAllLines = lines?.map((line) => {
      line['checked'] = false;
      return line;
    });
    setLines(unselectAllLines);
  }

  function getLinesForApplication() {
    const linesCopy = cloneDeep(lines);
    const existingLines = linesCopy.filter((line) => !line.new);
    if (!isLineSelected) {
      const allDraftLines = existingLines.filter(
        (line) => line.status === lineStatus.draft,
      );
      return allDraftLines;
    } else {
      const selectedLines = existingLines.filter((line) => line.checked);
      return selectedLines;
    }
  }

  function validatePlanLength(linesInApp: Array<ILine>) {
    const duration = getStartEndOfLines(linesInApp);
    const linesLength = dateDifference(
      moment(duration.start),
      moment(duration.end),
    );
    return linesLength > 182;
  }

  function setExistingPlan(planId: string) {
    const plan = applications.find((plan) => plan.id === planId);
    if (plan) {
      const existingPlanNewer =
        existingPlan &&
        moment(existingPlan?.createdAt) > moment(plan?.createdAt);
      if (!existingPlanNewer) {
        existingPlan = plan;
      }
    }
  }

  function getNumActiveLinesForProduct(productId: string) {
    const activeLinesForProduct = lines.filter(
      (line) =>
        line.product === productId &&
        (line.status === lineStatus.active ||
          line.status === lineStatus.complete),
    );
    if (activeLinesForProduct[0]) {
      setExistingPlan(activeLinesForProduct[0]?.financing_application);
    }
    return activeLinesForProduct?.length;
  }

  function validateNumLinesPerProduct(linesInApp: Array<ILine>) {
    const linesPerProduct = groupArrayByKey(linesInApp, 'product');
    let passesTest = true;
    for (const [product, productLinesInApp] of Object.entries(
      linesPerProduct,
    )) {
      const numActiveLinesForProduct = getNumActiveLinesForProduct(product);
      if (productLinesInApp.length + numActiveLinesForProduct < 3) {
        passesTest = false;
      }
    }
    return passesTest;
  }

  async function handleSubmit() {
    notification.removeNotification('application');
    let linesForApp = getLinesForApplication();
    const passedLinesPerProductTest = validateNumLinesPerProduct(linesForApp);
    if (!passedLinesPerProductTest) {
      return snackbar.show(messages.submitApplication.errors.productNumber);
    }
    if (!existingPlan) {
      const passedPlanLengthTest = validatePlanLength(linesForApp);
      if (!passedPlanLengthTest) {
        notification.addNotification({
          type: ENotificationTypes.application,
          message: messages.submitApplication.errors.planTooShort,
          learnMoreMessage: messages.submitApplication.errors.linesLength,
          icon: 'icons/snackbar/important.svg',
        });
        return;
      }
    }

    let url = '/submit-application';
    if (existingPlan) {
      url += `?plan=${existingPlan.id}&planname=${
        existingPlan.displayName || existingPlan.name
      }`;
    }
    if (linesForApp && linesForApp.length > 0) {
      url += existingPlan ? '&' : '?';
      url += `lines=${JSON.stringify(linesForApp.map((line) => line.id))}`;
    }
    unselectedLines();
    history.push(url);
  }

  const lineBeingEdited = lines?.find(
    (line) => line.edit || line.editActiveLine,
  );

  let disableButton = !hasDraftLine;
  let disableReason: string;
  if (lineBeingEdited && !disableButton) {
    disableButton = true;
  }
  if (isCreatingFirst3Lines()) {
    disableButton = true;
    disableReason = messages.lines.onboarding.firstPlanRequirements;
  }

  // const passedLinesOverlapTest = validateLinesOverlap(linesForApp);
  // if (!passedLinesOverlapTest) {
  //   notification.addNotification({
  //     type: ENotificationTypes.application,
  //     message: "Stock Shortage Risk",
  //     learnMoreMessage: (
  //       <span>
  //         Optimally planned supply chains tend to include partial overlap
  //         between Lines, with one Line starting before the previous one ends,
  //         ensuring stock is constantly replenished. <b>We recommend</b>{" "}
  //         editing your Lines to suit this structure.
  //       </span>
  //     ),
  //     icon: "icons/snackbar/lightBulb.svg",
  //   });
  // }

  return (
    <Button
      color="primary"
      variant="contained"
      wrapperClass={classes.root}
      disabled={disableButton}
      tooltipText={disableButton ? disableReason : undefined}
      onClick={handleSubmit}
      data-automation-id="submitApplication"
    >
      {buttonText}
    </Button>
  );
}
